import type { CreateInput, Entity, ID, UpdateInput } from '@/types';
import { createId, nowISO } from '@/utils/id';
import type { StorageAdapter } from './adapters';

export interface Repository<T extends Entity> {
  list(): Promise<T[]>;
  getById(id: ID): Promise<T | null>;
  create(input: CreateInput<T>): Promise<T>;
  createMany(inputs: CreateInput<T>[]): Promise<T[]>;
  update(id: ID, patch: UpdateInput<T>): Promise<T>;
  remove(id: ID): Promise<void>;
  removeWhere(predicate: (entity: T) => boolean): Promise<void>;
  replaceAll(entities: T[]): Promise<void>;
  clear(): Promise<void>;
}

export class NotFoundError extends Error {
  constructor(entity: string, id: ID) {
    super(`${entity} "${id}" no longer exists.`);
    this.name = 'NotFoundError';
  }
}

/**
 * One generic CRUD implementation shared by every entity, so all repositories
 * have identical, predictable semantics. Entities are stored as a single JSON
 * array per key — at QuotyLab's data volumes this is dramatically faster than
 * key-per-record and keeps reads atomic.
 */
export function createRepository<T extends Entity>(
  adapter: StorageAdapter,
  key: string,
  entityName: string,
  idPrefix: string,
): Repository<T> {
  const readAll = async (): Promise<T[]> => (await adapter.read<T[]>(key)) ?? [];
  const writeAll = (entities: T[]): Promise<void> => adapter.write(key, entities);

  const materialise = (input: CreateInput<T>): T => {
    const timestamp = nowISO();
    return {
      ...(input as object),
      id: (input as { id?: ID }).id ?? createId(idPrefix),
      createdAt: timestamp,
      updatedAt: timestamp,
    } as T;
  };

  return {
    async list() {
      return readAll();
    },

    async getById(id) {
      const entities = await readAll();
      return entities.find((entity) => entity.id === id) ?? null;
    },

    async create(input) {
      const entities = await readAll();
      const entity = materialise(input);
      await writeAll([...entities, entity]);
      return entity;
    },

    async createMany(inputs) {
      const entities = await readAll();
      const created = inputs.map(materialise);
      await writeAll([...entities, ...created]);
      return created;
    },

    async update(id, patch) {
      const entities = await readAll();
      const index = entities.findIndex((entity) => entity.id === id);
      if (index === -1) throw new NotFoundError(entityName, id);

      const current = entities[index] as T;
      const next = { ...current, ...patch, updatedAt: nowISO() } as T;
      const draft = [...entities];
      draft[index] = next;
      await writeAll(draft);
      return next;
    },

    async remove(id) {
      const entities = await readAll();
      await writeAll(entities.filter((entity) => entity.id !== id));
    },

    async removeWhere(predicate) {
      const entities = await readAll();
      await writeAll(entities.filter((entity) => !predicate(entity)));
    },

    async replaceAll(entities) {
      await writeAll(entities);
    },

    async clear() {
      await adapter.remove(key);
    },
  };
}
